import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    BookOpen, Rocket, ListChecks, UserPlus, Building2, Settings, ChevronRight,
    LayoutGrid, Contact, Activity, UsersRound, Briefcase, Shield, Phone, 
    FileText, Bell, User, Upload, Filter, Pencil, Split, Link2, Key,
    Globe, Search, CheckCircle, ArrowRight, ChevronDown, ChevronUp,
    Code, Copy, Check, AlertTriangle, Info, Layers, Eye, EyeOff,
    MousePointer, Table2, Hash, Mail, PhoneCall, MapPin, MessageSquare,
    Image, DollarSign, ArrowLeftRight, Monitor, Wifi
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";

// Copy button component for code blocks
function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button
            onClick={handleCopy}
            className="absolute top-2 right-2 p-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-white transition-colors"
            title="Copy to clipboard"
        >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
    );
}

// Code block component
function CodeBlock({ language, children }) {
    return (
        <div className="relative group rounded-lg overflow-hidden my-3">
            <div className="flex items-center justify-between bg-slate-800 px-4 py-1.5 border-b border-slate-700">
                <span className="text-xs text-slate-400 font-mono">{language}</span>
            </div>
            <pre className="bg-slate-900 p-4 overflow-x-auto text-sm">
                <code className="text-slate-200 font-mono whitespace-pre">{children}</code>
            </pre>
            <CopyButton text={children} />
        </div>
    );
}

// Table of Contents sidebar item
function TocItem({ id, label, icon: Icon, active, onClick, indent = false }) {
    return (
        <button
            onClick={() => onClick(id)}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all text-left ${
                active
                    ? "bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-600"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            } ${indent ? "pl-8" : ""}`}
        >
            {Icon && <Icon className={`h-4 w-4 flex-shrink-0 ${active ? "text-blue-600" : "text-slate-400"}`} />}
            <span className="truncate">{label}</span>
        </button>
    );
}

// Permission table
function PermissionTable({ headers, rows }) {
    return (
        <div className="border rounded-lg overflow-hidden my-3">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-slate-50 border-b">
                        {headers.map((h, i) => (
                            <th key={i} className="px-4 py-2.5 text-left font-semibold text-slate-700">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                            {row.map((cell, j) => (
                                <td key={j} className="px-4 py-2.5 text-slate-600 border-t border-slate-100">
                                    {j === 0 ? <span className="font-medium text-slate-800">{cell}</span> : cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Callout / Tip box
function Callout({ type = "info", title, children }) {
    const styles = {
        info: { bg: "bg-blue-50 border-blue-200", icon: Info, iconColor: "text-blue-600", titleColor: "text-blue-800" },
        tip: { bg: "bg-emerald-50 border-emerald-200", icon: CheckCircle, iconColor: "text-emerald-600", titleColor: "text-emerald-800" },
        warning: { bg: "bg-amber-50 border-amber-200", icon: AlertTriangle, iconColor: "text-amber-600", titleColor: "text-amber-800" },
    };
    const s = styles[type];
    const Icon = s.icon;
    return (
        <div className={`${s.bg} border rounded-lg p-4 my-4 flex gap-3`}>
            <Icon className={`h-5 w-5 ${s.iconColor} flex-shrink-0 mt-0.5`} />
            <div>
                {title && <p className={`font-semibold ${s.titleColor} mb-1`}>{title}</p>}
                <div className="text-sm text-slate-700 leading-relaxed">{children}</div>
            </div>
        </div>
    );
}

// Step card for the onboarding guide
function StepCard({ number, title, icon: Icon, children }) {
    return (
        <div className="flex gap-4 mb-6">
            <div className="flex-shrink-0 flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-bold shadow-md">
                    {number}
                </div>
                <div className="w-0.5 flex-1 bg-blue-200 mt-2" />
            </div>
            <div className="flex-1 pb-6">
                <div className="flex items-center gap-2 mb-2">
                    <Icon className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-semibold text-slate-800">{title}</h4>
                </div>
                <div className="text-sm text-slate-600 leading-relaxed space-y-2">
                    {children}
                </div>
            </div>
        </div>
    );
}

const sections = [
    { id: "getting-started", label: "Getting Started", icon: Rocket },
    { id: "onboarding", label: "Admin Onboarding", icon: Settings, indent: true },
    { id: "roles", label: "User Roles", icon: Shield, indent: true },
    { id: "navigation", label: "Navigation", icon: Layers, indent: true },
    { id: "dashboard", label: "Dashboard", icon: LayoutGrid },
    { id: "leads", label: "Leads", icon: Contact },
    { id: "lead-filters", label: "Filtering", icon: Filter, indent: true },
    { id: "lead-create", label: "Creating Leads", icon: Pencil, indent: true },
    { id: "lead-bulk", label: "Bulk Operations", icon: Split, indent: true },
    { id: "lead-call", label: "Call Modal", icon: Phone, indent: true },
    { id: "affiliator-leads", label: "Affiliator Leads", icon: Activity },
    { id: "users", label: "Users", icon: UsersRound },
    { id: "leads-visibility", label: "Leads Visibility", icon: Eye, indent: true },
    { id: "desks", label: "Desks / Floors", icon: Briefcase },
    { id: "settings-admin", label: "Settings", icon: Settings },
    { id: "permissions", label: "Permissions", icon: Shield },
    { id: "affiliate-api", label: "Affiliate API", icon: Code },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "profile", label: "Profile", icon: User },
];

export function Documentation() {
    const { user } = useAuth();
    const [activeSection, setActiveSection] = useState("getting-started");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const contentRef = useRef(null);

    const scrollToSection = (id) => {
        setActiveSection(id);
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // Observe which section is in view
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                        break;
                    }
                }
            },
            { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
        );

        sections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="flex h-[calc(100vh-64px)] overflow-hidden bg-slate-50">
            {/* Sidebar - Table of Contents */}
            <aside className={`flex-shrink-0 border-r bg-white transition-all duration-200 ${sidebarCollapsed ? "w-0 overflow-hidden" : "w-64"}`}>
                <div className="p-4 border-b">
                    <div className="flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-blue-600" />
                        <h2 className="font-semibold text-slate-800">Documentation</h2>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">SimpleCRM User Guide</p>
                </div>
                <nav className="p-2 overflow-y-auto h-[calc(100%-73px)] space-y-0.5">
                    {sections.map((s) => (
                        <TocItem
                            key={s.id}
                            id={s.id}
                            label={s.label}
                            icon={s.icon}
                            active={activeSection === s.id}
                            onClick={scrollToSection}
                            indent={s.indent}
                        />
                    ))}
                </nav>
            </aside>

            {/* Main Content */}
            <main ref={contentRef} className="flex-1 overflow-y-auto scroll-smooth">
                <div className="max-w-4xl mx-auto px-6 py-8 space-y-12">

                    {/* Hero */}
                    <div className="text-center pb-6 border-b">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white mb-4 shadow-lg">
                            <BookOpen className="h-8 w-8" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-2">SimpleCRM Documentation</h1>
                        <p className="text-slate-500 max-w-lg mx-auto">
                            Everything you need to know about setting up and using SimpleCRM — from initial configuration to daily operations.
                        </p>
                    </div>

                    {/* ============================================ */}
                    {/* SECTION: Getting Started */}
                    {/* ============================================ */}
                    <section id="getting-started">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-blue-100">
                                <Rocket className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Getting Started</h2>
                                <p className="text-sm text-slate-500">Login, roles, and finding your way around</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Logging In</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed">
                                        Navigate to the login page and enter your credentials (email + password). If <strong>Fast Login</strong> is enabled by an admin,
                                        you can use a direct login link without needing a password. After login you'll be directed to the <strong>Dashboard</strong>.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Admin Onboarding */}
                    {/* ============================================ */}
                    <section id="onboarding">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-emerald-100">
                                <Settings className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Administrator Onboarding Guide</h2>
                                <p className="text-sm text-slate-500">First-time setup — follow these steps in order</p>
                            </div>
                        </div>

                        <Callout type="info" title="Before You Begin">
                            The administrator must complete this one-time setup before anyone else can start using the CRM. Follow the steps in order — each step depends on the previous one.
                        </Callout>

                        <Card>
                            <CardContent className="p-6">
                                <StepCard number="1" title="Configure Lead Statuses" icon={ListChecks}>
                                    <p>Go to <Badge variant="outline" className="mx-1">Lead Management</Badge> in the sidebar. Create the statuses your team will use — for example: <em>New, Contacted, Interested, Not Interested, Converted</em>.</p>
                                    <p>Each status requires a <strong>label</strong>, a <strong>value</strong>, and a <strong>color</strong>. Drag-and-drop to reorder. These statuses appear in every dropdown, filter, and lead card across the system.</p>
                                </StepCard>

                                <StepCard number="2" title="Create Positions with Permissions" icon={Shield}>
                                    <p>Go to <Badge variant="outline" className="mx-1">Positions</Badge> in the sidebar. Positions define what each user can and cannot do. Create at least one position per role:</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                                        <li><strong>Admin</strong> — Role: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">admin</code>. Full system access.</li>
                                        <li><strong>Floor Manager</strong> — Role: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">floorManager</code>. Oversees multiple desks on a floor.</li>
                                        <li><strong>Desk Manager</strong> — Role: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">deskManager</code>. Manages a single desk and its agents.</li>
                                        <li><strong>Agent</strong> — Role: <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">agent</code>. Works directly on leads.</li>
                                    </ul>
                                    <p className="mt-2">For each position, set <strong>default permissions</strong>:</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                                        <li><strong>Leads</strong> — view, create, update, delete, view email, view phone, view action history</li>
                                        <li><strong>Staff</strong> — view, create, update, delete (usually off for agents)</li>
                                        <li><strong>Desks</strong> — view, create, update, delete (usually off for agents)</li>
                                        <li><strong>Grid Columns</strong> — which columns are visible in the leads table</li>
                                        <li><strong>Editable Fields</strong> — which lead fields the user can modify</li>
                                        <li><strong>VOIP Providers</strong> — which calling services the user can access</li>
                                    </ul>
                                    <Callout type="tip" title="Pro Tip">
                                        Create multiple agent positions if different teams need different permissions — e.g., "Conversion Agent" with full phone visibility vs. "Retention Agent" with restricted fields.
                                    </Callout>
                                </StepCard>

                                <StepCard number="3" title="Register Users" icon={UserPlus}>
                                    <p>Go to <Badge variant="outline" className="mx-1">Users</Badge> → <strong>Create</strong>. For each user, fill in:</p>
                                    <ol className="list-decimal list-inside space-y-1 ml-2 mt-2">
                                        <li>Name, email, and password</li>
                                        <li>Select the <strong>Position</strong> you created in Step 2 (permissions auto-fill)</li>
                                        <li>Leave the desk assignment empty for now — you'll assign desks in Step 4</li>
                                        <li>Optionally configure VOIP credentials if calling is set up</li>
                                    </ol>
                                    <Callout type="warning" title="Important">
                                        Create Floor Managers and Desk Managers <strong>first</strong>, then Agents. Managers must exist before you can assign them to desks.
                                    </Callout>
                                </StepCard>

                                <StepCard number="4" title="Create Desks" icon={Building2}>
                                    <p>Go to <Badge variant="outline" className="mx-1">Desks / Floors</Badge> and create your desk structure:</p>
                                    <ol className="list-decimal list-inside space-y-1 ml-2 mt-2">
                                        <li>Click <strong>Create Desk</strong></li>
                                        <li>Enter a name (e.g., "Sales Desk 1", "Retention Desk A")</li>
                                        <li>Select the <strong>Floor</strong> (Conversion or Retention)</li>
                                        <li>Assign a <strong>Desk Manager</strong></li>
                                        <li>Assign a <strong>Floor Manager</strong></li>
                                    </ol>
                                    <p className="mt-2">After creating desks, go back to <strong>Users</strong> and edit each agent/desk manager to assign them to their respective desk.</p>
                                </StepCard>

                                <StepCard number="5" title="Configure System Settings (Optional)" icon={Settings}>
                                    <p>Go to <Badge variant="outline" className="mx-1">Settings</Badge> to optionally configure:</p>
                                    <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                                        <li><strong>IP Whitelist</strong> — Restrict system access to specific IPs</li>
                                        <li><strong>VOIP Providers</strong> — Set up calling integrations (Voiso, CommPeak, Coperato, SquareTalk)</li>
                                        <li><strong>Affiliate API Keys</strong> — Generate keys for external lead sources</li>
                                    </ul>
                                </StepCard>

                                <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200 mt-2">
                                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                                    <div>
                                        <p className="font-semibold text-emerald-800">You're Ready!</p>
                                        <p className="text-sm text-emerald-700">Once positions, users, and desks are set up, your team can log in and start working on leads.</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: User Roles */}
                    {/* ============================================ */}
                    <section id="roles">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-purple-100">
                                <Shield className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">User Roles</h2>
                                <p className="text-sm text-slate-500">Understanding what each role can do</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <PermissionTable
                                    headers={["Role", "Description"]}
                                    rows={[
                                        ["Admin", "Full access. Manages users, desks, settings, positions, lead statuses, and system configuration."],
                                        ["Floor Manager", "Manages multiple desks on a floor. Can assign/unassign leads, view team stats, and manage desk managers & agents."],
                                        ["Desk Manager", "Manages a single desk and its agents. Can view and manage leads assigned to the desk."],
                                        ["Agent", "Works on assigned leads. Can view/update leads, make calls, add comments, and manage their pipeline."],
                                    ]}
                                />
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Navigation */}
                    {/* ============================================ */}
                    <section id="navigation">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-slate-100">
                                <Layers className="h-6 w-6 text-slate-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Navigation</h2>
                                <p className="text-sm text-slate-500">The sidebar menu structure</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        { icon: LayoutGrid, name: "Dashboard", desc: "Overview stats & desk breakdown", roles: "All" },
                                        { icon: Contact, name: "Leads", desc: "Full lead management", roles: "All" },
                                        { icon: Activity, name: "Affiliator Leads", desc: "Live leads from affiliate API", roles: "Admin" },
                                        { icon: UsersRound, name: "Users", desc: "User management", roles: "Admin, Managers" },
                                        { icon: Briefcase, name: "Desks / Floors", desc: "Desk & floor structure", roles: "Admin, Managers" },
                                        { icon: Settings, name: "Settings", desc: "System configuration", roles: "Admin" },
                                        { icon: ListChecks, name: "Lead Management", desc: "Lead status configuration", roles: "Admin" },
                                        { icon: Shield, name: "Positions", desc: "Role & permission management", roles: "Admin" },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-slate-50 transition">
                                            <div className="p-2 rounded-md bg-slate-100">
                                                <item.icon className="h-4 w-4 text-slate-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-slate-800">{item.name}</p>
                                                <p className="text-xs text-slate-500 truncate">{item.desc}</p>
                                            </div>
                                            <Badge variant="outline" className="text-[10px] flex-shrink-0">{item.roles}</Badge>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-sm text-slate-500 mt-4">The sidebar can be collapsed/expanded for more workspace.</p>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Dashboard */}
                    {/* ============================================ */}
                    <section id="dashboard">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-indigo-100">
                                <LayoutGrid className="h-6 w-6 text-indigo-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Dashboard</h2>
                                <p className="text-sm text-slate-500">Key metrics at a glance</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    The dashboard displays key metrics filtered by time range (<strong>Today, Week, Month, Year,</strong> or <strong>Custom</strong> date range).
                                </p>

                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Views by Role</h3>
                                    <div className="space-y-2">
                                        {[
                                            { role: "Agent", desc: "Personal stats — total leads assigned, status breakdown." },
                                            { role: "Desk Manager", desc: "Team stats — agent count, team leads, desk overview." },
                                            { role: "Floor Manager", desc: "Floor-level overview — all desks, agent counts, lead distribution." },
                                            { role: "Admin", desc: "Full overview — total leads, agents, desks, plus lead import." },
                                        ].map((v, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <ChevronRight className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                                                <p className="text-sm text-slate-600"><strong>{v.role}:</strong> {v.desc}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Lead Import (Admin)</h3>
                                    <p className="text-sm text-slate-600 mb-2">Admins can import leads via CSV from the dashboard:</p>
                                    <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li>Click <strong>Import</strong> button</li>
                                        <li>Upload a CSV file (max 10 MB, max 15,000 leads per batch)</li>
                                        <li>Map columns (firstName, lastName, email, phone, country, status, source, etc.)</li>
                                        <li>Set defaults: status, country, desk, or floor manager for all imported leads</li>
                                        <li>Optionally enable <strong>duplicate checking</strong> (by email)</li>
                                        <li>Review preview, then confirm import</li>
                                    </ol>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Leads */}
                    {/* ============================================ */}
                    <section id="leads">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-cyan-100">
                                <Contact className="h-6 w-6 text-cyan-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Leads</h2>
                                <p className="text-sm text-slate-500">Viewing, managing, and working with leads</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    The Leads page displays all leads you have access to in a data table with:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        { icon: Table2, text: "Configurable columns (per user permissions)" },
                                        { icon: Hash, text: "Server-side pagination (100 per page, jump to page)" },
                                        { icon: ArrowLeftRight, text: "Sorting by date (created/updated)" },
                                        { icon: Search, text: "Search bar for quick text search (name, email, phone)" },
                                        { icon: Eye, text: "Color-coded status badges" },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-2 p-2 rounded bg-slate-50">
                                            <item.icon className="h-4 w-4 text-cyan-600" />
                                            <span className="text-sm text-slate-600">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Lead Filters */}
                    <section id="lead-filters">
                        <h3 className="text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Filter className="h-5 w-5 text-cyan-600" /> Filtering
                        </h3>
                        <Card>
                            <CardContent className="p-6 space-y-3">
                                <p className="text-sm text-slate-600">
                                    Click the <strong>Filter</strong> button to open the filter modal. Available filters:
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        "Status (multi-select)",
                                        "Desk (multi-select)",
                                        "Agent (multi-select, with desk filter)",
                                        "Floor Manager (multi-select, admin only)",
                                        "Source (multi-select, auto-populated)",
                                        "Affiliate (multi-select)",
                                        "Country (multi-select)",
                                        "Date Range (registered from/to)",
                                        "Live Lead indicator (yes/no)",
                                    ].map((f, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                            <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                                            {f}
                                        </div>
                                    ))}
                                </div>
                                <Callout type="tip" title="Filter Shortcuts">
                                    Save frequently used filter combinations as shortcuts for quick one-click access.
                                </Callout>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Lead Create */}
                    <section id="lead-create">
                        <h3 className="text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Pencil className="h-5 w-5 text-cyan-600" /> Creating & Editing Leads
                        </h3>
                        <Card>
                            <CardContent className="p-6 space-y-3">
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1">Creating a Lead</h4>
                                    <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li>Go to <strong>Leads → Create Lead</strong> (or click the + button)</li>
                                        <li>Fill in: First Name, Last Name, Email, Phone, Second Phone, Country, City, Address, Status, Source, Extra/Notes</li>
                                        <li>Optionally assign to a Desk, Agent, or Floor Manager</li>
                                        <li>Save</li>
                                    </ol>
                                </div>
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1">Editing a Lead</h4>
                                    <p className="text-sm text-slate-600">Click on a lead row to open the <strong>Call Modal</strong>, or use the edit icon to go to the edit page. Editable fields depend on your position's permissions.</p>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Bulk Operations */}
                    <section id="lead-bulk">
                        <h3 className="text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Split className="h-5 w-5 text-cyan-600" /> Bulk Operations
                        </h3>
                        <Card>
                            <CardContent className="p-6 space-y-5">
                                {/* Edit */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Edit</Badge>
                                    </h4>
                                    <p className="text-sm text-slate-600 mb-1">Select leads → <strong>Modify → Edit</strong>:</p>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li><strong>Change Status</strong> — Update status for all selected leads</li>
                                        <li><strong>Change Source</strong> — Update source field</li>
                                        <li><strong>Change Country</strong> — Update country field</li>
                                        <li><strong>Delete Leads</strong> — Permanently remove (requires delete permission)</li>
                                    </ul>
                                </div>

                                {/* Assign */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Assign</Badge>
                                    </h4>
                                    <p className="text-sm text-slate-600 mb-1">Select leads → <strong>Modify → Assign</strong>:</p>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li><strong>To User</strong> — Assign to a specific agent (optionally filter by desk)</li>
                                        <li><strong>To Desk</strong> — Assign to a desk (admin/floor manager)</li>
                                        <li><strong>To Floor Manager</strong> — Assign to a floor manager (admin only)</li>
                                    </ul>
                                </div>

                                {/* Unassign */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                                        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Unassign</Badge>
                                    </h4>
                                    <p className="text-sm text-slate-600 mb-1">Select leads → <strong>Modify → Unassign</strong>:</p>
                                    <p className="text-sm text-slate-600">Three independent checkboxes let you unassign from any combination:</p>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li>☐ <strong>Floor Manager</strong> — Remove floor manager only</li>
                                        <li>☐ <strong>Desk</strong> — Remove desk only</li>
                                        <li>☐ <strong>User</strong> — Remove agent only</li>
                                    </ul>
                                </div>

                                {/* Split Leads */}
                                <div>
                                    <h4 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                                        <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Split Leads</Badge>
                                    </h4>
                                    <p className="text-sm text-slate-600 mb-1">Select leads → <strong>Split Leads</strong>:</p>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li><strong>Between Agents</strong> — Evenly distribute among chosen agents</li>
                                        <li><strong>Between Desks</strong> — Evenly distribute among chosen desks</li>
                                        <li>Filter by status before splitting</li>
                                        <li>Preview distribution before confirming</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Call Modal */}
                    <section id="lead-call">
                        <h3 className="text-xl font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Phone className="h-5 w-5 text-cyan-600" /> Call Modal (Lead Detail)
                        </h3>
                        <Card>
                            <CardContent className="p-6">
                                <p className="text-sm text-slate-600 mb-3">Click the phone icon on any lead to open the full detail modal:</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {[
                                        { icon: FileText, text: "Lead Info — View & edit all fields inline" },
                                        { icon: PhoneCall, text: "VOIP Calling — Voiso, CommPeak, Coperato, SquareTalk" },
                                        { icon: MessageSquare, text: "Comments — Add, view, and delete with timestamps" },
                                        { icon: Image, text: "Screenshots — Upload & manage attachments" },
                                        { icon: DollarSign, text: "Balance — View/update lead balance & currency" },
                                        { icon: ArrowLeftRight, text: "Navigation — Arrow buttons to browse leads" },
                                    ].map((item, i) => (
                                        <div key={i} className="flex items-center gap-2 p-2 rounded bg-slate-50">
                                            <item.icon className="h-4 w-4 text-cyan-600" />
                                            <span className="text-sm text-slate-600">{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Affiliator Leads */}
                    {/* ============================================ */}
                    <section id="affiliator-leads">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-orange-100">
                                <Activity className="h-6 w-6 text-orange-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Affiliator Leads</h2>
                                <p className="text-sm text-slate-500">Live leads from affiliate integrations (Admin only)</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6 space-y-3">
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    A dedicated page showing leads received through the <strong>Affiliate API</strong>. This page is only visible to Admins.
                                </p>
                                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                    <li>Automatically filtered to show only leads with an affiliate source</li>
                                    <li>Live leads are marked with an <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-xs">L</Badge> indicator</li>
                                    <li>Same table, filter, and bulk-operation capabilities as the main Leads page</li>
                                    <li>Real-time notifications when new affiliate leads arrive</li>
                                </ul>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Users */}
                    {/* ============================================ */}
                    <section id="users">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-teal-100">
                                <UsersRound className="h-6 w-6 text-teal-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Users</h2>
                                <p className="text-sm text-slate-500">Managing team members</p>
                            </div>
                        </div>
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Viewing Users</h3>
                                    <p className="text-sm text-slate-600">Table of all system users with: name, email, position, role, desk, and status (active/inactive).</p>
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Creating a User</h3>
                                    <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li>Go to <strong>Users → Create</strong></li>
                                        <li>Fill in: First Name, Last Name, Email, Password</li>
                                        <li>Select a <strong>Position</strong> (defines role and default permissions)</li>
                                        <li>Assign to a <strong>Desk</strong> (for agents/desk managers)</li>
                                        <li>Configure <strong>Permissions</strong> (leads, staff, desks, grid columns, editable fields, VOIP)</li>
                                        <li>Set <strong>Leads Visibility</strong> (see below)</li>
                                        <li>Optionally configure VOIP credentials (extension IDs for each provider)</li>
                                        <li>Enable/disable <strong>Fast Login</strong></li>
                                        <li>Save</li>
                                    </ol>
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Editing a User</h3>
                                    <p className="text-sm text-slate-600">Click edit on any user to modify their details, permissions, desk assignment, or VOIP configuration.</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Leads Visibility */}
                        <Card id="leads-visibility">
                            <CardContent className="p-6 space-y-4">
                                <div className="flex items-center gap-2 mb-1">
                                    <Eye className="h-5 w-5 text-teal-600" />
                                    <h3 className="text-lg font-semibold text-slate-800">Leads Visibility</h3>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    When creating or editing a user, the <strong>Leads Visibility</strong> section controls which leads the user can see. This is a critical setting that determines the user's data access scope.
                                </p>

                                <PermissionTable
                                    headers={["Scope", "What The User Sees"]}
                                    rows={[
                                        [
                                            "Assigned",
                                            "Only leads directly assigned to this user. For agents, this means leads where they are the assigned agent. For desk managers, leads in their managed desk(s). For floor managers, leads in desks they manage."
                                        ],
                                        [
                                            "Desk",
                                            "Leads belonging to specific desks you select. When you choose this option, a multi-select \"Allowed Desks\" dropdown appears — pick one or more desks. The user will see all leads in those desks, regardless of who the assigned agent is."
                                        ],
                                        [
                                            "Floor",
                                            "All leads assigned to this user as floor manager. Only relevant for floor managers — shows every lead where this user is set as the floor manager."
                                        ],
                                        [
                                            "All",
                                            "The user can see every lead in the entire system, with no restrictions. Use with caution — typically reserved for admin-level users or senior managers who need full visibility."
                                        ],
                                    ]}
                                />

                                <Callout type="tip" title="Best Practice">
                                    For agents, use <strong>Assigned</strong> so they only see their own leads. For desk managers, <strong>Assigned</strong> shows their desk's leads. Use <strong>Desk</strong> if a user needs cross-desk visibility without being a manager of those desks.
                                </Callout>

                                <Callout type="warning" title="Important">
                                    The visibility scope works <em>in addition</em> to role-based filtering. An agent set to "All" will see all leads, but their edit/delete capabilities are still governed by their position's permissions.
                                </Callout>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Desks / Floors */}
                    {/* ============================================ */}
                    <section id="desks">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-amber-100">
                                <Briefcase className="h-6 w-6 text-amber-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Desks / Floors</h2>
                                <p className="text-sm text-slate-500">Organizational structure</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6 space-y-4">
                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Structure</h3>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li><strong>Floors</strong> — Conversion, Retention (organizational grouping)</li>
                                        <li><strong>Desks</strong> — Belong to a floor. Each desk has a Desk Manager and a Floor Manager</li>
                                        <li><strong>Agents</strong> — Assigned to desks</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-slate-800 mb-2">Managing Desks</h3>
                                    <p className="text-sm text-slate-600">Admins and floor managers can:</p>
                                    <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                        <li>Create new desks (name, floor, manager, floor manager)</li>
                                        <li>Edit desk details</li>
                                        <li>Delete desks</li>
                                        <li>View agents assigned to each desk</li>
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Settings (Admin) */}
                    {/* ============================================ */}
                    <section id="settings-admin">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-rose-100">
                                <Settings className="h-6 w-6 text-rose-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Settings (Admin Only)</h2>
                                <p className="text-sm text-slate-500">System configuration, lead statuses, and positions</p>
                            </div>
                        </div>

                        {/* System Config */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-4">
                                <h3 className="text-lg font-semibold text-slate-800">System Configuration</h3>

                                <div>
                                    <h4 className="font-semibold text-slate-700 mb-1 flex items-center gap-2">
                                        <Monitor className="h-4 w-4 text-slate-500" /> IP Whitelist
                                    </h4>
                                    <p className="text-sm text-slate-600">Add IP addresses that are allowed to access the system. Block access from unauthorized IPs.</p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 mb-1 flex items-center gap-2">
                                        <Wifi className="h-4 w-4 text-slate-500" /> VOIP Providers
                                    </h4>
                                    <p className="text-sm text-slate-600">
                                        Configure Voiso, CommPeak, Coperato, and SquareTalk. Enable/disable providers system-wide.
                                        Set API keys, base URLs, and caller IDs per provider.
                                    </p>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 mb-1 flex items-center gap-2">
                                        <Key className="h-4 w-4 text-slate-500" /> Affiliate API Keys
                                    </h4>
                                    <p className="text-sm text-slate-600">
                                        Generate API keys for affiliate integrations. Set name, default status for new leads, and allowed origins.
                                        View API documentation and track lead count per API key.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Lead Management */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800">Lead Management</h3>
                                <p className="text-sm text-slate-600">
                                    Located at <Badge variant="outline" className="mx-1">Lead Management</Badge> in the sidebar:
                                </p>
                                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                    <li>Create, edit, reorder, and delete lead statuses</li>
                                    <li>Set status label, value, and color</li>
                                    <li>Drag-and-drop to reorder statuses</li>
                                    <li>Statuses appear throughout the system in filters, dropdowns, and lead cards</li>
                                </ul>
                            </CardContent>
                        </Card>

                        {/* Positions */}
                        <Card>
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800">Positions</h3>
                                <p className="text-sm text-slate-600">
                                    Located at <Badge variant="outline" className="mx-1">Positions</Badge> in the sidebar:
                                </p>
                                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                    <li>Create custom positions (e.g., "Senior Agent", "Team Lead")</li>
                                    <li>Each position maps to a <strong>role</strong> (admin, floorManager, deskManager, agent)</li>
                                    <li>Set a <strong>floor</strong> (conversion/retention)</li>
                                    <li>Define <strong>default permissions</strong> for the position</li>
                                    <li>When creating a user with this position, they inherit these default permissions</li>
                                </ul>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Permissions */}
                    {/* ============================================ */}
                    <section id="permissions">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-violet-100">
                                <Shield className="h-6 w-6 text-violet-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Permissions Reference</h2>
                                <p className="text-sm text-slate-500">All available permission categories</p>
                            </div>
                        </div>

                        {/* Lead Permissions */}
                        <Card className="mb-4">
                            <CardContent className="p-0">
                                <div className="px-6 pt-5 pb-2">
                                    <h3 className="text-base font-semibold text-slate-800">Lead Permissions</h3>
                                </div>
                                <PermissionTable
                                    headers={["Permission", "Description"]}
                                    rows={[
                                        ["View", "Can see leads"],
                                        ["Create", "Can create new leads"],
                                        ["Update", "Can edit lead details"],
                                        ["Delete", "Can delete leads"],
                                        ["View Email", "Can see lead email addresses"],
                                        ["View Phone", "Can see lead phone numbers"],
                                        ["View Action History", "Can see lead activity/comment history"],
                                    ]}
                                />
                            </CardContent>
                        </Card>

                        {/* Staff Permissions */}
                        <Card className="mb-4">
                            <CardContent className="p-0">
                                <div className="px-6 pt-5 pb-2">
                                    <h3 className="text-base font-semibold text-slate-800">Staff Permissions</h3>
                                </div>
                                <PermissionTable
                                    headers={["Permission", "Description"]}
                                    rows={[
                                        ["View", "Can see user list"],
                                        ["Create", "Can create new users"],
                                        ["Update", "Can edit users"],
                                        ["Delete", "Can delete users"],
                                    ]}
                                />
                            </CardContent>
                        </Card>

                        {/* Desk Permissions */}
                        <Card className="mb-4">
                            <CardContent className="p-0">
                                <div className="px-6 pt-5 pb-2">
                                    <h3 className="text-base font-semibold text-slate-800">Desk Permissions</h3>
                                </div>
                                <PermissionTable
                                    headers={["Permission", "Description"]}
                                    rows={[
                                        ["View", "Can see desk list"],
                                        ["Create", "Can create desks"],
                                        ["Update", "Can edit desks"],
                                        ["Delete", "Can delete desks"],
                                    ]}
                                />
                            </CardContent>
                        </Card>

                        {/* Grid Columns */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-2">
                                <h3 className="text-base font-semibold text-slate-800">Grid Columns (Lead Table Visibility)</h3>
                                <p className="text-sm text-slate-600">Controls which columns are visible in the leads table:</p>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {["Source", "Affiliate", "Manager", "Registered Date", "Modified Date", "Desk", "Floor Manager", "Last Comment", "Last Comment Date", "Last Comment Manager", "Age"].map((col) => (
                                        <Badge key={col} variant="outline" className="text-xs">{col}</Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Editable Fields */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-2">
                                <h3 className="text-base font-semibold text-slate-800">Editable Lead Fields</h3>
                                <p className="text-sm text-slate-600">Controls which lead fields the user can modify:</p>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {["First Name", "Last Name", "Username", "Email", "Phone", "Second Phone", "Status", "Country", "City", "Address", "Source", "Extra/Notes"].map((f) => (
                                        <Badge key={f} variant="outline" className="text-xs">{f}</Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* VOIP */}
                        <Card>
                            <CardContent className="p-6 space-y-2">
                                <h3 className="text-base font-semibold text-slate-800">VOIP Permissions</h3>
                                <p className="text-sm text-slate-600">Controls which calling providers the user can use:</p>
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                    {["CommPeak", "Voiso", "Coperato", "SquareTalk"].map((p) => (
                                        <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Affiliate API */}
                    {/* ============================================ */}
                    <section id="affiliate-api">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-fuchsia-100">
                                <Code className="h-6 w-6 text-fuchsia-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Affiliate API Integration</h2>
                                <p className="text-sm text-slate-500">For affiliators — push leads into SimpleCRM via API</p>
                            </div>
                        </div>

                        <Callout type="info" title="Who is this for?">
                            This section is for <strong>affiliators</strong> (external partners) who want to integrate their systems to automatically send leads into SimpleCRM. If you are a CRM user, you can skip this section — your admin will set up the API keys.
                        </Callout>

                        {/* Overview */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-4">
                                <h3 className="text-lg font-semibold text-slate-800">Overview</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    The Affiliate API allows external systems to create leads, retrieve their submitted leads, and check lead statuses.
                                    All API requests require an <strong>API key</strong> provided by the CRM administrator.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[
                                        { method: "POST", path: "Create Lead", desc: "Submit a new lead" },
                                        { method: "GET", path: "My Leads", desc: "List your submitted leads" },
                                        { method: "GET", path: "Lead Status", desc: "Check a specific lead's status" },
                                    ].map((ep, i) => (
                                        <div key={i} className="p-3 rounded-lg border bg-slate-50">
                                            <Badge className={`text-xs mb-1 ${ep.method === "POST" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-blue-100 text-blue-700 hover:bg-blue-100"}`}>
                                                {ep.method}
                                            </Badge>
                                            <p className="text-sm font-semibold text-slate-800">{ep.path}</p>
                                            <p className="text-xs text-slate-500">{ep.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Authentication */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <Key className="h-5 w-5 text-fuchsia-600" /> Authentication
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Include your API key in every request using one of the following headers:
                                </p>
                                <CodeBlock language="http">{`Authorization: Bearer YOUR_API_KEY`}</CodeBlock>
                                <p className="text-sm text-slate-500">or alternatively:</p>
                                <CodeBlock language="http">{`x-api-key: YOUR_API_KEY`}</CodeBlock>
                                <Callout type="warning" title="Keep Your Key Safe">
                                    Your API key grants full access to create leads and view your lead data. Never expose it in client-side code or public repositories.
                                </Callout>
                            </CardContent>
                        </Card>

                        {/* Create Lead */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">POST</Badge>
                                    Create Lead
                                </h3>
                                <p className="text-sm text-slate-600">Submit a new lead to the CRM.</p>
                                <CodeBlock language="http">{`POST /lead/v1/public
Content-Type: application/json
Authorization: Bearer YOUR_API_KEY

{
  "firstName": "John",
  "lastName": "Doe",
  "email": "john@example.com",
  "phone": "+1234567890",
  "country": "United States",
  "source": "Campaign Name",
  "comment": "Optional note"
}`}</CodeBlock>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Request Fields</h4>
                                    <PermissionTable
                                        headers={["Field", "Type", "Required", "Description"]}
                                        rows={[
                                            ["firstName", "string", "No", "Lead's first name"],
                                            ["lastName", "string", "No", "Lead's last name"],
                                            ["email", "string", "Yes", "Lead's email address (must be unique)"],
                                            ["phone", "string", "No", "Lead's phone number"],
                                            ["secondPhone", "string", "No", "Lead's secondary phone"],
                                            ["country", "string", "No", "Lead's country"],
                                            ["city", "string", "No", "Lead's city"],
                                            ["address", "string", "No", "Lead's address"],
                                            ["source", "string", "No", "Campaign or source name"],
                                            ["comment", "string", "No", "Initial comment/note"],
                                        ]}
                                    />
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Success Response (200)</h4>
                                    <CodeBlock language="json">{`{
  "status": 200,
  "data": {
    "RESULT": "SUCCESS",
    "message": "Lead created successfully",
    "lead": {
      "id": "64f1a2b3c4d5e6f7a8b9c0d1",
      "email": "john@example.com",
      "firstName": "John",
      "lastName": "Doe",
      "status": "New",
      "affiliate": "YourAffiliateName"
    }
  }
}`}</CodeBlock>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Error Response — Duplicate Email (400)</h4>
                                    <CodeBlock language="json">{`{
  "status": 400,
  "data": {
    "RESULT": "ERROR",
    "message": "A lead with this email address already exists"
  }
}`}</CodeBlock>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Get My Leads */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">GET</Badge>
                                    Get My Leads
                                </h3>
                                <p className="text-sm text-slate-600">Retrieve a paginated list of leads you have submitted.</p>
                                <CodeBlock language="http">{`GET /lead/v1/public/my-leads?page=1&limit=50&status=New
Authorization: Bearer YOUR_API_KEY`}</CodeBlock>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Query Parameters</h4>
                                    <PermissionTable
                                        headers={["Parameter", "Type", "Default", "Description"]}
                                        rows={[
                                            ["page", "integer", "1", "Page number"],
                                            ["limit", "integer", "50", "Results per page"],
                                            ["status", "string", "—", "Filter by lead status"],
                                            ["email", "string", "—", "Filter by email (partial match)"],
                                            ["fromDate", "date", "—", "Filter leads created after this date"],
                                            ["toDate", "date", "—", "Filter leads created before this date"],
                                        ]}
                                    />
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Success Response (200)</h4>
                                    <CodeBlock language="json">{`{
  "status": 200,
  "data": {
    "leads": [
      {
        "_id": "64f1a2b3c4d5e6f7a8b9c0d1",
        "firstName": "John",
        "lastName": "Doe",
        "email": "john@example.com",
        "phone": "+1234567890",
        "status": "New",
        "createdAt": "2025-01-15T10:30:00.000Z",
        "updatedAt": "2025-01-15T10:30:00.000Z"
      }
    ],
    "pagination": {
      "page": 1,
      "limit": 50,
      "totalCount": 142,
      "totalPages": 3
    }
  },
  "message": "Leads retrieved successfully"
}`}</CodeBlock>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Get Lead Status */}
                        <Card className="mb-4">
                            <CardContent className="p-6 space-y-3">
                                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">GET</Badge>
                                    Get Lead Status
                                </h3>
                                <p className="text-sm text-slate-600">Check the current status of a specific lead.</p>
                                <CodeBlock language="http">{`GET /lead/v1/public/status/:leadId
Authorization: Bearer YOUR_API_KEY`}</CodeBlock>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Success Response (200)</h4>
                                    <CodeBlock language="json">{`{
  "status": 200,
  "data": {
    "id": "64f1a2b3c4d5e6f7a8b9c0d1",
    "email": "john@example.com",
    "firstName": "John",
    "lastName": "Doe",
    "status": "Contacted",
    "createdAt": "2025-01-15T10:30:00.000Z",
    "updatedAt": "2025-01-16T14:20:00.000Z"
  },
  "message": "Lead status retrieved successfully"
}`}</CodeBlock>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-slate-700 text-sm mb-2">Not Found Response (404)</h4>
                                    <CodeBlock language="json">{`{
  "status": 404,
  "data": {
    "RESULT": "ERROR",
    "message": "Lead not found or access denied"
  }
}`}</CodeBlock>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Error Codes */}
                        <Card>
                            <CardContent className="p-0">
                                <div className="px-6 pt-5 pb-2">
                                    <h3 className="text-base font-semibold text-slate-800">HTTP Status Codes</h3>
                                </div>
                                <PermissionTable
                                    headers={["Code", "Meaning"]}
                                    rows={[
                                        ["200", "Success — request completed"],
                                        ["400", "Bad Request — validation error (e.g., duplicate email, missing email)"],
                                        ["401", "Unauthorized — invalid, missing, or inactive API key"],
                                        ["404", "Not Found — lead does not exist or access denied"],
                                        ["500", "Server Error — internal error, please retry"],
                                    ]}
                                />
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Notifications */}
                    {/* ============================================ */}
                    <section id="notifications">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-sky-100">
                                <Bell className="h-6 w-6 text-sky-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Notifications</h2>
                                <p className="text-sm text-slate-500">Real-time alerts</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6">
                                <p className="text-sm text-slate-600 mb-3">The system sends real-time notifications for:</p>
                                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                    <li>New lead assignments</li>
                                    <li>Lead status changes</li>
                                    <li>Live leads received from affiliates</li>
                                </ul>
                                <p className="text-sm text-slate-600 mt-3">
                                    Notifications appear via the bell icon in the sidebar. Click to view and dismiss.
                                </p>
                            </CardContent>
                        </Card>
                    </section>

                    {/* ============================================ */}
                    {/* SECTION: Profile */}
                    {/* ============================================ */}
                    <section id="profile">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 rounded-lg bg-slate-100">
                                <User className="h-6 w-6 text-slate-600" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Profile</h2>
                                <p className="text-sm text-slate-500">Your account</p>
                            </div>
                        </div>
                        <Card>
                            <CardContent className="p-6">
                                <p className="text-sm text-slate-600 mb-3">Click your avatar in the sidebar to access:</p>
                                <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 ml-2">
                                    <li><strong>View Profile</strong> — See your account details, role, desk, and permissions</li>
                                    <li><strong>Edit Profile</strong> — Update your personal information</li>
                                    <li><strong>Sign Out</strong> — Log out of the system</li>
                                </ul>
                            </CardContent>
                        </Card>
                    </section>

                    {/* Footer */}
                    <div className="text-center py-8 border-t text-sm text-slate-400">
                        SimpleCRM Documentation — Last updated February 2026
                    </div>
                </div>
            </main>
        </div>
    );
}
